<template>
  <div :class="[$style.stateBox, { [$style.noOverflow]: !overflow }]">
    <div
      v-if="shouldShowLoadingOrError"
      :class="{ [$style.overlay]: showAsOverlay }"
      class="d-flex align-items-center justify-content-center p-4"
    >
      <SCAInlineBanner
        v-if="hasError"
        type="error"
        :title="$t('common.message.errorError2')"
        :description="$t('common.message.componentLoadError')"
      >
        <slot v-if="$slots['action']" name="action" />
      </SCAInlineBanner>
      <SCALoader v-else-if="showLoadingState" :class="$style.loader" />
    </div>
    <template v-if="shouldShowContent">
      <slot />
    </template>
    <div v-if="busy" :class="$style.busyOverlay" test-id="state-box-busy-overlay" />
  </div>
</template>

<script lang="ts" setup>
import { debouncedRef, toRef } from "@vueuse/core";
import { computed } from "vue";

import SCAInlineBanner from "./SCAInlineBanner.vue";
import SCALoader from "./SCALoader.vue";

const props = withDefaults(
  defineProps<{
    overlay?: boolean;
    loading?: boolean;
    hasError?: boolean;
    overflow?: boolean;
    loadingDebounce?: number;
    busy?: boolean;
  }>(),
  {
    overlay: false,
    loading: false,
    hasError: false,
    overflow: true,
    loadingDebounce: 0,
    busy: false,
  },
);

// Fix TypeScript error by converting props.loading to ref first
const debouncedLoading = debouncedRef(toRef(props, "loading"), props.loadingDebounce);

// Cache computed properties for better performance
const showLoadingState = computed(() => props.loading && debouncedLoading.value);

const showAsOverlay = computed(() => {
  // Early returns for better performance
  if (props.hasError) return false;
  return props.overlay && props.loading;
});

// Memoize template conditions to avoid repeated evaluations
const shouldShowContent = computed(() => !props.hasError && (!showLoadingState.value || showAsOverlay.value));
const shouldShowLoadingOrError = computed(() => props.hasError || showLoadingState.value);
</script>

<style module lang="scss">
.stateBox {
  position: relative;
  overflow: auto;
  max-height: 100%;

  &.noOverflow {
    overflow: unset;
  }

  .overlay {
    z-index: var(--sca-state-box-overlay-zindex, 2);
    // background-color: rgba(255, 255, 255, 0.75);
    background-color: transparent;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
}
.loader {
  display: flex;
  height: 100%;
}

.busyOverlay {
  position: absolute;
  inset: 0;
  z-index: 7;
  background-color: var(--color-background-default);
  opacity: 0.6;
  cursor: progress;
  pointer-events: auto;
}
</style>
